/**
 * Packing List PDF — layout cercano al documento comercial (sin fondos de color).
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ptcImage from '../../imagenes/ptc.jpg';
import summaryStampImage from '../../imagenes/palmtrade-summary-stamp.png';
import { parseExcelNumber } from './parsePackingListExcel';
import { invoiceNumberCorrelativo } from '../invoice/invoiceNumberUtils';

const COMPANY_LINES = [
  'PALM TRADE CHILE SPA',
  'Avenida Presidente Kennedy 5146, oficina 51',
  'Vitacura, Santiago, Chile',
  'Fono: (56-2) 22190119 - Fax: (56-2) 22184390',
];

const RUT_EMPRESA = '77.710.540-K';

const SUMMARY_TABLE_ID = 'packing-list-summary';

/** ~1 línea a 8pt en mm: columna izquierda baja para que el texto central arranque más arriba. */
const SUMMARY_LEFT_EXTRA_TOP_PAD = 3.6;

/** Elimina hojas finales vacías que jsPDF/autoTable pueden dejar al paginar. */
function removeTrailingBlankPages(doc) {
  while (doc.internal.getNumberOfPages() > 1) {
    const n = doc.internal.getNumberOfPages();
    doc.setPage(n);
    const pageData = doc.internal.pages[n];
    const content = Array.isArray(pageData) ? pageData.join('') : String(pageData || '');
    const hasText = /\)\s*Tj/.test(content) || /\)\s*'\s/.test(content);
    const hasImage = /\/I\d+\s+Do/.test(content);
    const hasFill = /\bre\s+B\s+f\s+re\s+S\b/.test(content) || /\bre\s+W\s+n\s+re\s+f\b/.test(content);
    if (hasText || hasImage || hasFill) break;
    doc.deletePage(n);
  }
}

const GRID = [0, 0, 0];
const LINE = 0.12;

function fmtNumCL(v, decimals = 3) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(v));
}

/** Enteros con separador de miles (es-CL), sin decimales — p. ej. Total pieces. */
function fmtIntegerCL(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v));
}

/** Width / Length: sin decimales, miles con coma (ej. 1220 → 1,220). */
function fmtMeasureWidthLength(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v));
}

/** Totales Summary: etiquetas a la izquierda, números alineados a la derecha (sin BUNDLES). */
function summaryTotalsCellLines(doc, innerWidthMm, bundlesValue, sumM3, sumPesoGross) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const spW = Math.max(0.2, doc.getTextWidth(' ') || 0.35);
  const rows = [
    ['Total Bundles', String(bundlesValue)],
    ['Total cbm or m³', fmtNumCL(sumM3, 3)],
    ['Total Gross Weight (Kg)', fmtNumCL(sumPesoGross, 3)],
  ];
  const labelColW =
    Math.max(...rows.map(([label]) => doc.getTextWidth(`${label}:`))) + spW;
  /** Borde derecho de los números: mitad del espacio libre tras las etiquetas. */
  const valueRightX = labelColW + (innerWidthMm - labelColW) * 0.5;

  return rows
    .map(([label, v]) => {
      const l = `${label}:`;
      const vs = String(v);
      const padAfterLabel = Math.max(0, Math.floor((labelColW - doc.getTextWidth(l)) / spW));
      const padBeforeValue = Math.max(
        0,
        Math.floor((valueRightX - labelColW - doc.getTextWidth(vs)) / spW)
      );
      return `${l}${' '.repeat(padAfterLabel)}${' '.repeat(padBeforeValue)}${vs}`;
    })
    .join('\n');
}

/** HS en línea tipo "4408 90 90" a partir de NCM sin espacios. */
function formatHsSummaryLine(hs) {
  const s = safe(hs).replace(/\s/g, '');
  if (!s) return '—';
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)} ${s.slice(4, 6)} ${s.slice(6, 8)}`;
  return safe(hs).trim();
}

/**
 * Columna central del Summary: cuatro líneas en texto plano (producto, destino, origen, HS).
 */
function summaryMiddleBlockText(desc, paisOrigen, hsCode, lugarEntrega, nombrePuertoDestino, paisDestino) {
  const line1 = desc.trim().replace(/\s+/g, ' ').toUpperCase() || '—';
  const entrega = lugarEntrega && String(lugarEntrega).trim();
  const line2 = entrega
    ? entrega.toUpperCase()
    : [
        normalizePortLabelForPackingList(nombrePuertoDestino),
        normalizeCountryForPackingList(paisDestino),
      ].filter(Boolean).join(', ').toUpperCase() || '—';
  const line3 = `COUNTRY OF ORIGIN: ${normalizeCountryForPackingList(paisOrigen).toUpperCase() || '—'}`;
  const line4 = `HS CODE ${formatHsSummaryLine(hsCode)}`;
  return [line1, line2, line3, line4].join('\n');
}

function fmtDateLong(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

function safe(s) {
  return s == null ? '' : String(s);
}

// Parche exclusivo de Packing List: Brasil -> Brazil.
function normalizeCountryForPackingList(s) {
  const t = safe(s).trim();
  if (!t) return '';
  return /^brasil$/i.test(t) ? 'Brazil' : t;
}

function normalizePortLabelForPackingList(label) {
  const t = safe(label).trim();
  if (!t) return '';
  return t.replace(/\((\s*Brasil\s*)\)/i, '(Brazil)');
}

/** Nombre de puerto sin sufijo " (País)" — p. ej. Loading Port en PDF. */
function portNameWithoutCountry(label) {
  const t = safe(label).trim();
  if (!t) return '';
  return t.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/** "Puerto (País)" → "Puerto, País" — p. ej. Unloading Port en PDF. */
function portNameCommaCountry(label) {
  const t = normalizePortLabelForPackingList(label);
  if (!t) return '';
  const m = t.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!m) return t;
  const port = m[1].trim();
  const country = normalizeCountryForPackingList(m[2].trim());
  return country ? `${port}, ${country}` : port;
}

/** Valores en mayúsculas como en el documento impreso. */
function toUpperDisplay(s) {
  const t = safe(s).trim();
  return t ? t.toUpperCase() : '—';
}

function withPinCodeAboveCountry(addrLines, clienteCodigoPostal) {
  const lines = [...addrLines];
  const hasPin = lines.some((ln) => /^PIN CODE\s*:/i.test(ln));
  const pin = safe(clienteCodigoPostal).trim();
  if (!pin || hasPin) return lines;

  const pinLine = `PIN CODE: ${pin}`.toUpperCase();
  if (lines.length === 0) return [pinLine];

  // En snapshot usamos último renglón como país; insertar PIN justo arriba.
  lines.splice(Math.max(0, lines.length - 1), 0, pinLine);
  return lines;
}

function netVal(r) {
  const n = r.peso_neto != null && r.peso_neto !== '' ? Number(r.peso_neto) : null;
  if (n != null && !Number.isNaN(n)) return n;
  return Number(r.peso) || 0;
}

function grossStr(r) {
  return r.peso != null ? fmtNumCL(r.peso, 3) : '';
}

function netStr(r) {
  const n = r.peso_neto != null && r.peso_neto !== '' ? Number(r.peso_neto) : null;
  if (n != null && !Number.isNaN(n)) return fmtNumCL(n, 3);
  return r.peso != null ? fmtNumCL(r.peso, 3) : '';
}

function cellEspesor(r) {
  const e = r.espesor;
  if (e == null || e === '') return '';
  if (typeof e === 'number' && !Number.isNaN(e)) return `${fmtNumCL(e, 1)} MM`;
  const n = parseFloat(String(e).replace(',', '.'));
  return Number.isFinite(n) ? `${fmtNumCL(n, 1)} MM` : safe(e);
}

function cellNum3CL(v) {
  if (v == null || v === '') return '';
  return fmtNumCL(v, 3);
}

function cbmPerBundleStr(r) {
  const totalM3 = Number(r.total_m3);
  const bundles = Number(r.cantidad_paquetes);
  if (!Number.isFinite(totalM3) || !Number.isFinite(bundles) || bundles === 0) return '';
  return fmtNumCL(totalM3 / bundles, 3);
}

function productsCell(r) {
  const t = safe(r.productos_texto);
  if (t) return t;
  return (
    [safe(r.grado_celda), safe(r.proveedor_celda), safe(r.almacen_celda)].filter(Boolean).join(' / ') ||
    '—'
  );
}

function isRwNrRow(r) {
  const txt = [safe(r.productos_texto), safe(r.grado_celda)].join(' ').toUpperCase();
  return /\b(RW\/NR|RW|WR)\b/.test(txt);
}

function measureWidthLengthCell(val) {
  const n = parseExcelNumber(val);
  return n != null ? fmtMeasureWidthLength(n) : '';
}

function widthCell(r) {
  const w = measureWidthLengthCell(r.ancho);
  if (w) return w;
  const txt = [safe(r.productos_texto), safe(r.grado_celda)].join(' ').toUpperCase();
  return /\bRW\b/.test(txt) ? 'RW' : safe(r.ancho);
}

function containerPdf(r) {
  const c = safe(r.container_celda);
  if (c) return c;
  return safe(r._container_excel) || (r._container != null ? String(r._container) : '');
}

function detailContainerKey(r) {
  return containerPdf(r).trim();
}

/** Fila vacía entre bloques de contenedor (bordes visibles, sin relleno). */
function detailContainerSeparatorRow() {
  return Array.from({ length: 13 }, () => ({
    content: '',
    styles: {
      minCellHeight: 2.2,
      fillColor: [255, 255, 255],
      lineColor: GRID,
      lineWidth: LINE,
    },
  }));
}

function sameMergeGroup(a, b) {
  const ca = safe(a.container_celda).trim() || safe(a._container_excel).trim();
  const cb = safe(b.container_celda).trim() || safe(b._container_excel).trim();
  if (!ca || ca !== cb) return false;
  return grossStr(a) === grossStr(b) && netStr(a) === netStr(b);
}

/**
 * Suma bruto/neto contando una vez por grupo que el PDF fusiona (mismas celdas Gross/Net
 * y contenedor). Si el peso es total de contenedor repetido en cada subfila, un reduce
 * simple lo multiplicaba.
 */
function sumPesoGrossByMergeGroups(detalleExcel) {
  let s = 0;
  let i = 0;
  while (i < detalleExcel.length) {
    let j = i + 1;
    while (j < detalleExcel.length && sameMergeGroup(detalleExcel[j - 1], detalleExcel[j])) {
      j += 1;
    }
    s += Number(detalleExcel[i].peso) || 0;
    i = j;
  }
  return s;
}

function sumPesoNetByMergeGroups(detalleExcel) {
  let s = 0;
  let i = 0;
  while (i < detalleExcel.length) {
    let j = i + 1;
    while (j < detalleExcel.length && sameMergeGroup(detalleExcel[j - 1], detalleExcel[j])) {
      j += 1;
    }
    s += netVal(detalleExcel[i]);
    i = j;
  }
  return s;
}

/**
 * @param {object} params
 * @param {string} [params.productDescription]
 * @param {string} [params.hsCode]
 */
export function generatePackingListPdf(params) {
  const {
    numeroInvoice,
    fechaEmbarque,
    clienteNombre,
    clienteDireccion,
    clienteCodigoPostal,
    poLabel,
    paisOrigen,
    paisDestino,
    nombreNave,
    nombrePuertoOrigen,
    nombrePuertoDestino,
    lugarEntrega,
    nombreBl,
    totalPaquetes,
    totalContenedores,
    detalleExcel = [],
    totalVolumenM3,
    productDescription = '',
    hsCode = '',
  } = params;

  /** A4 horizontal: más ancho para la tabla de detalle al imprimir. */
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentW = pageW - 2 * margin;
  const rightX = pageW - margin;

  const logoW = 28;
  const imgProps = doc.getImageProperties(ptcImage);
  const logoH = (imgProps.height * logoW) / imgProps.width;
  let y = 10;

  doc.addImage(ptcImage, 'JPEG', margin, y, logoW, logoH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Packing List', rightX, y + 14, { align: 'right' });
  doc.setFontSize(13);
  const inv = invoiceNumberCorrelativo(numeroInvoice) || '—';
  doc.text(inv, rightX, y + 21, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Date: ${fmtDateLong(fechaEmbarque)}`, rightX, y + 27, { align: 'right' });

  y = y + logoH + 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  COMPANY_LINES.forEach((line) => {
    doc.text(line, margin, y);
    y += 3.4;
  });
  y += 4;

  const addrLinesRaw = safe(clienteDireccion)
    .split(/\r?\n/)
    .map((ln) => ln.trim())
    .filter(Boolean)
    .map((ln) => ln.toUpperCase());
  const addrLines = withPinCodeAboveCountry(addrLinesRaw, clienteCodigoPostal);
  const customerBlock = ['Customer:', toUpperDisplay(clienteNombre), ...addrLines].join('\n');

  /** Bloque cliente + envío: una sola grilla (sin hueco), 75% del ancho de página, alineada al margen izquierdo. */
  const blockW = pageW * 0.75;
  const blockRightMargin = pageW - margin - blockW;
  const shipColRatios = [18, 18, 30, 76, 32, 34, 34, 35];
  const shipColRatioSum = shipColRatios.reduce((a, b) => a + b, 0);
  const shipColumnStyles = {};
  for (let i = 0; i < 8; i++) {
    shipColumnStyles[i] = { cellWidth: (blockW * shipColRatios[i]) / shipColRatioSum };
  }

  const shipHeaders = [
    'Total Packages',
    'Container',
    'Destination Country',
    'Vessel Name',
    'Loading Port',
    'Unloading Port',
    'Place of delivery',
    'BL',
  ];
  const shipRow = [
    safe(totalPaquetes) || '—',
    totalContenedores !== '' && totalContenedores != null ? safe(totalContenedores) : '',
    toUpperDisplay(normalizeCountryForPackingList(paisDestino)),
    toUpperDisplay(nombreNave),
    toUpperDisplay(portNameWithoutCountry(nombrePuertoOrigen)),
    toUpperDisplay(portNameCommaCountry(nombrePuertoDestino)),
    toUpperDisplay(lugarEntrega),
    toUpperDisplay(nombreBl),
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: blockRightMargin },
    tableWidth: blockW,
    body: [
      [
        {
          content: customerBlock,
          rowSpan: 2,
          colSpan: 5,
          styles: { fontSize: 8, valign: 'top', halign: 'left' },
        },
        { content: 'Contract Number', styles: { fontStyle: 'bold', halign: 'left', valign: 'middle', fontSize: 8 } },
        { content: toUpperDisplay(poLabel), colSpan: 2, styles: { halign: 'center', valign: 'middle', fontSize: 8 } },
      ],
      [
        { content: 'Country of Origin', styles: { fontStyle: 'bold', halign: 'left', valign: 'middle', fontSize: 8 } },
        { content: toUpperDisplay(normalizeCountryForPackingList(paisOrigen)), colSpan: 2, styles: { halign: 'center', valign: 'middle', fontSize: 8 } },
      ],
      shipHeaders.map((h) => ({
        content: h,
        styles: {
          fontStyle: 'bold',
          fontSize: 7,
          halign: 'center',
          valign: 'middle',
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
      })),
      shipRow.map((cell) => ({
        content: cell,
        styles: { fontSize: 7, halign: 'center', valign: 'middle', fontStyle: 'normal' },
      })),
    ],
    columnStyles: shipColumnStyles,
    styles: {
      lineColor: GRID,
      lineWidth: LINE,
      textColor: [0, 0, 0],
      cellPadding: 1.1,
      valign: 'middle',
    },
    theme: 'grid',
  });

  y = doc.lastAutoTable.finalY + 5;

  const sumM3 =
    totalVolumenM3 != null
      ? Number(totalVolumenM3)
      : detalleExcel.reduce((s, r) => s + (Number(r.total_m3) || 0), 0);
  const sumPesoGross = sumPesoGrossByMergeGroups(detalleExcel);
  const sumPesoNet = sumPesoNetByMergeGroups(detalleExcel);
  const bundlesTxt = totalPaquetes ? `${totalPaquetes} BUNDLES` : '—';
  const bundlesSummaryTxt =
    totalPaquetes != null && totalPaquetes !== '' ? fmtIntegerCL(totalPaquetes) : '—';
  const totalBundlesNumberOnly =
    totalPaquetes != null && totalPaquetes !== '' ? String(totalPaquetes) : '—';

  const headRow1 = [
    { content: 'Contract', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Products', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Bundles', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Measures', colSpan: 3, styles: { halign: 'center', valign: 'middle' } },
    { content: 'Pieces per\nBundles', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Total\npieces', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'cbm or m3\nper Bundles', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Total Vol.\ncbm or m3', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Gross\nWeight kgs', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Net\nWeight kgs', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Container', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
  ];

  const headRow2 = [{ content: 'Thickness' }, { content: 'Width' }, { content: 'Length' }];

  const bodyRows = buildDetailBody(detalleExcel);

  /** Pie: solo número en Bundles; "X BUNDLES" junto en Measures (3 cols); vol. total y pesos. */
  const footRow = [
    '',
    '',
    totalBundlesNumberOnly,
    {
      content: bundlesTxt,
      colSpan: 3,
      styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' },
    },
    '',
    '',
    '',
    fmtNumCL(sumM3, 3),
    fmtNumCL(sumPesoGross, 3),
    fmtNumCL(sumPesoNet, 3),
    '',
  ];

  if (bodyRows.length === 0) {
    bodyRows.push([
      '—',
      'Sin líneas cargadas desde Excel',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ]);
  }

  /** Tabla de detalle: 13 columnas (misma anchura útil que Summary). Ratios proporcionales, suma = contentW. */
  const detailColRatios = [18, 30, 12, 11, 11, 11, 13, 12, 13, 13, 13, 13, 24];
  const detailColRatioSum = detailColRatios.reduce((a, b) => a + b, 0);
  const detailColumnStyles = {};
  for (let i = 0; i < detailColRatios.length; i++) {
    detailColumnStyles[i] = { cellWidth: (contentW * detailColRatios[i]) / detailColRatioSum };
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [headRow1, headRow2],
    body: bodyRows,
    foot: [footRow],
    showFoot: 'lastPage',
    styles: {
      fontSize: 6.5,
      cellPadding: 0.9,
      valign: 'middle',
      halign: 'center',
      lineColor: GRID,
      lineWidth: LINE,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 6.5,
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontSize: 6.5,
      fontStyle: 'bold',
      lineColor: GRID,
    },
    columnStyles: detailColumnStyles,
    theme: 'grid',
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        const d = data.doc;
        d.setFontSize(7);
        d.setTextColor(80, 80, 80);
        d.text(`Packing List ${inv}`, margin, 8);
        d.setTextColor(0, 0, 0);
      }
    },
  });

  y = doc.lastAutoTable.finalY + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Summary', margin, y + 2);
  y += 5;

  const desc = safe(productDescription) || productsCell(detalleExcel[0] || {}) || '—';
  const hs = safe(hsCode) || '—';

  const summaryCol0InnerW = contentW * 0.34 - 4;
  const totalsCellContent = summaryTotalsCellLines(
    doc,
    summaryCol0InnerW,
    bundlesSummaryTxt,
    sumM3,
    sumPesoGross
  );
  const summaryMiddleContent = summaryMiddleBlockText(
    desc,
    paisOrigen,
    hs,
    lugarEntrega,
    nombrePuertoDestino,
    paisDestino
  );

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableId: SUMMARY_TABLE_ID,
    body: [
      [
        {
          content: totalsCellContent,
          styles: { valign: 'top', halign: 'left', fontStyle: 'normal' },
        },
        {
          content: summaryMiddleContent,
          styles: { valign: 'top', halign: 'left', fontStyle: 'normal' },
        },
        {
          content: '',
          styles: { valign: 'middle', halign: 'center', minCellHeight: 28 },
        },
      ],
    ],
    columnStyles: {
      0: {
        cellWidth: contentW * 0.34,
        fontSize: 8,
        valign: 'top',
        halign: 'left',
        lineWidth: 0,
        cellPadding: { top: SUMMARY_LEFT_EXTRA_TOP_PAD, left: 1, right: 1, bottom: 1 },
      },
      1: {
        cellWidth: contentW * 0.38,
        fontSize: 8,
        valign: 'top',
        halign: 'left',
        lineWidth: 0,
        cellPadding: { top: 0, left: 1, right: 1, bottom: 1 },
      },
      2: {
        cellWidth: contentW * 0.28,
        fontSize: 8,
        valign: 'top',
        halign: 'center',
        lineWidth: 0,
        cellPadding: { top: 0, left: 1, right: 1, bottom: 1 },
      },
    },
    styles: { lineWidth: 0, halign: 'left' },
    bodyStyles: {
      fontSize: 8,
      valign: 'top',
      halign: 'left',
      lineWidth: 0,
    },
    tableLineWidth: 0,
    theme: 'plain',
    didDrawCell: (data) => {
      const { doc: d, cell, table, section, column, row } = data;
      if (table.id !== SUMMARY_TABLE_ID) return;
      if (section !== 'body' || row.index !== 0 || column.index !== 2) return;
      const props = d.getImageProperties(summaryStampImage);
      const pad = 1;
      const maxW = Math.max(4, cell.width - 2 * pad);
      const maxH = Math.max(4, cell.height - 2 * pad);
      let w = maxW;
      let h = (props.height * w) / props.width;
      if (h > maxH) {
        h = maxH;
        w = (props.width * h) / props.height;
      }
      const x = cell.x + (cell.width - w) / 2;
      const yTop = cell.y + pad;
      d.addImage(summaryStampImage, 'PNG', x, yTop, w, h);
    },
  });

  removeTrailingBlankPages(doc);

  const fname = `PackingList_${safe(inv).replace(/\W/g, '_') || 'export'}.pdf`;
  doc.save(fname);
}

/**
 * Respuesta GET /packinglists/:id → parámetros de {@link generatePackingListPdf}.
 */
export function packingListFromApiToPdfParams(api) {
  if (!api) return {};
  return {
    numeroInvoice: api.numero_invoice || '',
    fechaEmbarque: api.fecha_embarque || '',
    clienteNombre: api.cliente_nombre_snapshot || '',
    clienteDireccion: api.cliente_direccion_snapshot || '',
    clienteCodigoPostal: api.cliente_codigo_postal || '',
    poLabel: api.po_label_snapshot || '',
    paisOrigen: api.pais_origen || '',
    paisDestino: api.pais_destino || '',
    nombreNave: api.nombre_nave || '',
    nombrePuertoOrigen: api.nombre_puerto_origen || '',
    nombrePuertoDestino: api.nombre_puerto_destino || '',
    lugarEntrega: api.lugar_entrega || '',
    nombreBl: api.nombre_bl || '',
    totalPaquetes: api.total_paquetes,
    totalContenedores: api.total_contenedores,
    detalleExcel: api.lineas || [],
    totalVolumenM3: api.total_volumen_m3,
    productDescription: api.product_description_snapshot || '',
    hsCode: api.hs_code_snapshot || '',
  };
}

/**
 * Filas del detalle: 13 columnas; Gross / Net / Container con rowSpan si el grupo consecutivo coincide.
 */
function buildDetailBody(detalleExcel) {
  const out = [];
  let i = 0;
  let lastContainerKey = null;

  while (i < detalleExcel.length) {
    const groupContainerKey = detailContainerKey(detalleExcel[i]);
    if (
      lastContainerKey != null &&
      groupContainerKey &&
      groupContainerKey !== lastContainerKey
    ) {
      out.push(detailContainerSeparatorRow());
    }
    if (groupContainerKey) lastContainerKey = groupContainerKey;

    let j = i + 1;
    while (j < detalleExcel.length && sameMergeGroup(detalleExcel[j - 1], detalleExcel[j])) {
      j += 1;
    }
    const span = j - i;
    const merge = span > 1;

    for (let k = 0; k < span; k += 1) {
      const r = detalleExcel[i + k];
      const m3 = r.total_m3 != null ? cellNum3CL(r.total_m3) : '';
      const m3PerBundle = cbmPerBundleStr(r);
      const base = [
        safe(r.nf_celda),
        productsCell(r),
        r.cantidad_paquetes != null ? String(r.cantidad_paquetes) : '',
        cellEspesor(r),
        widthCell(r),
        measureWidthLengthCell(r.largo),
        safe(r.piezas_por_paquete),
        isRwNrRow(r) ? '' : (r.total_piezas != null ? fmtIntegerCL(r.total_piezas) : ''),
        m3PerBundle,
        m3,
      ];

      if (merge && k === 0) {
        base.push(
          { content: grossStr(r), rowSpan: span, styles: { valign: 'middle' } },
          { content: netStr(r), rowSpan: span, styles: { valign: 'middle' } },
          { content: containerPdf(r), rowSpan: span, styles: { valign: 'middle' } }
        );
        out.push(base);
      } else if (merge && k > 0) {
        out.push(base);
      } else {
        base.push(grossStr(r), netStr(r), containerPdf(r));
        out.push(base);
      }
    }
    i = j;
  }
  return out;
}
